import { useState, useEffect } from 'react';
import { Button, buttonClassName } from '@safetyhub/ui';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { groupsApi, Group } from '../../lib/groups';
import { canReceiveSiteTransfer } from '../../lib/membershipAccess';
import {
  getTransferByToken,
  acceptTransfer,
  rejectTransfer,
  SiteTransferInfo,
} from '../../lib/site-transfer';
import { extractApiError } from '../../lib/api';
import { useToastStore } from '../../stores/toast';

export default function SiteTransferAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { showToast } = useToastStore();

  const [transferInfo, setTransferInfo] = useState<SiteTransferInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // 수락 관련 상태
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [isDone, setIsDone] = useState(false);
  const [doneMessage, setDoneMessage] = useState('');

  // 이관 정보 로드 — error 상태가 설정된 후에는 재호출하지 않아 무한루프 방지
  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const loadTransferInfo = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const info = await getTransferByToken(token);
        if (!cancelled) setTransferInfo(info);
      } catch (err) {
        if (!cancelled) {
          const apiError = extractApiError(err);
          setLoadError(apiError.message || '이관 정보를 불러올 수 없습니다.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadTransferInfo();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // 선택한 조직의 그룹 목록 로드
  useEffect(() => {
    if (!selectedOrgId) {
      setGroups([]);
      setSelectedGroupId('');
      return;
    }

    setIsLoadingGroups(true);
    groupsApi
      .findAll(selectedOrgId)
      .then((data) => {
        setGroups(data);
        // 그룹이 1개면 자동 선택, 아니면 기본 그룹 선택
        if (data.length === 1) {
          setSelectedGroupId(data[0].id);
        } else {
          const defaultGroup = data.find((g) => g.isDefault);
          setSelectedGroupId(defaultGroup?.id ?? data[0]?.id ?? '');
        }
      })
      .catch(() => setGroups([]))
      .finally(() => setIsLoadingGroups(false));
  }, [selectedOrgId]);

  // 요청자 본인 여부 체크
  const isRequester = isAuthenticated && transferInfo && user?.id === transferInfo.requestedByUserId;

  // 이관받을 조직 후보 — P6-b: 옛 `role === 'OWNER'` → 권한 **key**(`site.transfer`).
  // 서버 `assertCanTransfer`와 같은 축(`SiteTransferAcceptModal`과 같은 판정, 그 함수 doc).
  const ownerMemberships = (user?.memberships ?? []).filter(canReceiveSiteTransfer);

  const handleAccept = async () => {
    if (!token) return;
    if (!selectedOrgId) {
      setActionError('이관 받을 조직을 선택해주세요.');
      return;
    }

    setActionError('');
    setIsAccepting(true);
    try {
      await acceptTransfer(token, {
        toOrganizationId: selectedOrgId,
        toGroupId: selectedGroupId || undefined,
      });
      setIsDone(true);
      setDoneMessage('이관을 수락했습니다. 현장이 선택한 조직으로 이동되었습니다.');
      showToast('현장 이관이 완료되었습니다.', 'success');
    } catch (err) {
      const apiError = extractApiError(err);
      setActionError(apiError.message);
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!token) return;

    setActionError('');
    setIsRejecting(true);
    try {
      await rejectTransfer(token);
      setIsDone(true);
      setDoneMessage('이관 요청을 거부했습니다.');
      showToast('이관 요청을 거부했습니다.', 'success');
    } catch (err) {
      const apiError = extractApiError(err);
      setActionError(apiError.message);
    } finally {
      setIsRejecting(false);
    }
  };

  // 로딩 중
  if (isLoading) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mx-auto" />
          <p className="mt-3 text-sm text-ink-500">이관 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 에러 (만료, 처리된 토큰 등)
  if (loadError) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-card shadow-sm max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="mt-4 text-lg font-semibold text-ink-900">이관 요청을 찾을 수 없습니다</h2>
          <p className="mt-2 text-sm text-ink-500">{loadError}</p>
          <p className="mt-1 text-xs text-ink-400">요청이 만료되었거나 이미 처리된 요청일 수 있습니다.</p>
          <Link to="/" className="mt-6 inline-block text-sm text-brand-600 hover:text-brand-800">
            홈으로 이동
          </Link>
        </div>
      </div>
    );
  }

  // 처리 완료
  if (isDone) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-card shadow-sm max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-4 text-lg font-semibold text-ink-900">완료</h2>
          <p className="mt-2 text-sm text-ink-600">{doneMessage}</p>
          {isAuthenticated ? (
            <Button variant="primary" onClick={() => navigate('/')} className="mt-6">
              홈으로 이동
            </Button>
          ) : (
            <Link to="/login" className="mt-6 inline-block text-sm text-brand-600 hover:text-brand-800">
              로그인하기
            </Link>
          )}
        </div>
      </div>
    );
  }

  // 미로그인 상태
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-card shadow-sm max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h2 className="mt-4 text-lg font-semibold text-ink-900">현장 이관 요청</h2>
          {transferInfo && (
            <div className="mt-3 text-sm text-ink-500 space-y-1">
              <p>
                <span className="font-medium text-ink-700">"{transferInfo.fromCompanyName}"</span>에서 현장 이관 요청이 왔습니다.
              </p>
              <p className="font-medium text-ink-800">{transferInfo.siteName}</p>
            </div>
          )}
          <p className="mt-4 text-sm text-ink-500">이관을 수락하려면 로그인이 필요합니다.</p>
          <Link
            to={`/login?redirect=/site-transfers/${token}`}
            className={buttonClassName('primary', 'md', 'mt-4 inline-flex')}
          >
            로그인하기
          </Link>
          <p className="mt-3 text-xs text-ink-400">
            계정이 없으신가요?{' '}
            <Link to={`/register?redirect=/site-transfers/${token}`} className="text-brand-600 hover:text-brand-800">
              회원가입
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // 요청자 본인이 접근한 경우
  if (isRequester) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-card shadow-sm max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="mt-4 text-lg font-semibold text-ink-900">본인이 요청한 이관입니다</h2>
          <p className="mt-2 text-sm text-ink-500">
            이 이관 요청은 본인이 보낸 것입니다. 받는 사람이 이 링크를 열어야 수락할 수 있습니다.
          </p>
          <Button variant="primary" onClick={() => navigate('/')} className="mt-6">
            홈으로 이동
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-card shadow-sm max-w-md w-full overflow-hidden">
        {/* 헤더 */}
        <div className="bg-brand-600 px-6 py-5 text-white">
          <h1 className="text-lg font-semibold">현장 이관 요청</h1>
          {transferInfo && (
            <p className="mt-1 text-sm text-brand-100">
              "{transferInfo.fromCompanyName}"에서 요청
            </p>
          )}
        </div>

        <div className="p-6 space-y-5">
          {/* 현장 정보 */}
          {transferInfo && (
            <div className="bg-ink-50 rounded-card p-4 space-y-2">
              <div>
                <p className="text-xs text-ink-500">현장명</p>
                <p className="text-sm font-semibold text-ink-900">{transferInfo.siteName}</p>
              </div>
              {transferInfo.siteAddress && (
                <div>
                  <p className="text-xs text-ink-500">주소</p>
                  <p className="text-sm text-ink-700">{transferInfo.siteAddress}</p>
                </div>
              )}
              <div className="flex gap-4 pt-1">
                <div>
                  <p className="text-xs text-ink-500">점검</p>
                  <p className="text-sm font-medium text-ink-900">{transferInfo.inspectionCount}건</p>
                </div>
                <div>
                  <p className="text-xs text-ink-500">보고서</p>
                  <p className="text-sm font-medium text-ink-900">{transferInfo.reportCount}건</p>
                </div>
              </div>
            </div>
          )}

          {/* 이관 받을 조직 선택 */}
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              이관 받을 조직
            </label>
            {ownerMemberships.length === 0 ? (
              <div className="bg-yellow-50 rounded-card px-3 py-2 text-sm text-yellow-700">
                Owner 권한이 있는 조직이 없습니다. 이관을 수락하려면 먼저 조직을 생성하세요.
              </div>
            ) : (
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="">조직 선택...</option>
                {ownerMemberships.map((m) => (
                  <option key={m.companyId} value={m.companyId}>
                    {m.company.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* 그룹 선택 — 그룹이 2개 이상일 때만 표시 */}
          {selectedOrgId && (
            <div>
              {isLoadingGroups ? (
                <div className="flex items-center gap-2 text-sm text-ink-500 py-2">
                  <div className="animate-spin h-4 w-4 border-2 border-brand-500 border-t-transparent rounded-full" />
                  그룹 목록 로딩 중...
                </div>
              ) : groups.length > 1 ? (
                <>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    이관 받을 그룹
                    <span className="ml-1 text-xs font-normal text-ink-400">(선택)</span>
                  </label>
                  <select
                    value={selectedGroupId}
                    onChange={(e) => setSelectedGroupId(e.target.value)}
                    className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    <option value="">그룹 없음 (기본)</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                        {g.isDefault ? ' (기본)' : ''}
                      </option>
                    ))}
                  </select>
                </>
              ) : null}
            </div>
          )}

          {/* 안내 */}
          <p className="text-xs text-ink-400 bg-ink-50 rounded-card px-3 py-2">
            수락 시 현장의 모든 점검 및 보고서가 선택한 조직으로 이동됩니다.
            이전 조직에서의 접근 권한은 사라집니다.
          </p>

          {/* 액션 에러 */}
          {actionError && (
            <p className="text-sm text-red-600 bg-red-50 rounded-card px-3 py-2">{actionError}</p>
          )}

          {/* 버튼 */}
          <div className="flex gap-3 pt-1">
            <Button
              variant="secondary"
              onClick={handleReject}
              disabled={isRejecting || isAccepting}
              className="flex-1"
            >
              {isRejecting ? '처리 중...' : '거부'}
            </Button>
            <Button
              variant="primary"
              onClick={handleAccept}
              disabled={isAccepting || isRejecting || !selectedOrgId || ownerMemberships.length === 0}
              className="flex-1"
            >
              {isAccepting ? '수락 중...' : '수락'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
